import React, { useState, useEffect, useCallback } from "react";
import {
  fetchPendingWordBankWords,
  moderateWordBankWord,
  deleteWordBankWord,
  fetchDefinitionFromAI,
  fetchExamplesFromAI,
  saveWordContent,
  patchWord,
  classifyWords,
} from "../api";

const POS_LABEL = { noun: "Noun", verb: "Verb", particle: "Particle" };

const FORM_LABEL = {
  past: "Past",
  present: "Present",
  imperative: "Imperative",
  masdar: "Masdar",
  ism_faail: "Active participle",
  ism_mafool: "Passive participle",
  plural: "Plural",
};

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "other", label: "Other" },
];

const POS_CHOICES = [
  { value: "noun", label: "Noun" },
  { value: "verb", label: "Verb" },
  { value: "particle", label: "Particle" },
];

/**
 * WordBankAdminReview — admin queue of learner-submitted Word Bank words.
 *
 * Per word the admin can:
 *  - Approve / Reject (pending) or Re-approve / Delete (rejected).
 *  - Edit the public definition + example sentences. AI fetches return a
 *    *preview* into editable fields (nothing is saved until the admin clicks
 *    Save), and the admin can also type/add/remove definition and examples
 *    manually. Saved content is public and shows in the word-detail popup.
 */
export default function WordBankAdminReview({ onClose }) {
  const [tab, setTab] = useState("pending");
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Inline content editor state (one word at a time).
  const [editingId, setEditingId] = useState(null);
  const [draftDef, setDraftDef] = useState("");
  const [draftPlural, setDraftPlural] = useState("");
  const [draftExamples, setDraftExamples] = useState([]); // [{ text, translation }]
  const [aiBusy, setAiBusy] = useState(null); // "definition" | "examples" | "apply"
  const [aiPreview, setAiPreview] = useState(null); // { wordId, data } — full AI result to verify
  const [saveBusy, setSaveBusy] = useState(false);
  const [editError, setEditError] = useState(null);

  // "Other" tab: manual/AI classification of blank-pos words.
  const [selected, setSelected] = useState(() => new Set());
  const [classifyBusy, setClassifyBusy] = useState(false); // batch in progress

  const load = useCallback(async (status) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPendingWordBankWords(status);
      setWords(data.words || []);
    } catch (e) {
      setError("Could not load words.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setConfirmDeleteId(null);
    setEditingId(null);
    setSelected(new Set());
    load(tab);
  }, [tab, load]);

  const removeFromList = (id) => setWords((prev) => prev.filter((w) => w.id !== id));
  const patchItem = (id, patch) =>
    setWords((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));

  const handleModerate = async (word, action) => {
    setBusyId(word.id);
    setError(null);
    try {
      await moderateWordBankWord(word.id, action);
      removeFromList(word.id); // leaves this tab (status changed)
    } catch {
      setError("Action failed. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (word) => {
    setBusyId(word.id);
    setError(null);
    try {
      await deleteWordBankWord(word.id);
      removeFromList(word.id);
    } catch {
      setError("Delete failed. Please try again.");
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  // ── "Other" tab: classify blank-pos words ───────────────────────────────────
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const dropClassified = (ids) => {
    const set = new Set(ids);
    setWords((prev) => prev.filter((w) => !set.has(w.id)));
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  };

  // Set a word's pos manually (admin write to shared public metadata).
  const setPosManual = async (word, pos) => {
    setBusyId(word.id);
    setError(null);
    try {
      await patchWord(word.id, { pos });
      dropClassified([word.id]); // no longer "Other"
    } catch (e) {
      setError(e.message || "Could not set the part of speech.");
    } finally {
      setBusyId(null);
    }
  };

  // AI-classify one or many words in a single call.
  const aiClassify = async (ids) => {
    if (!ids.length) return;
    if (ids.length === 1) setBusyId(ids[0]); else setClassifyBusy(true);
    setError(null);
    try {
      const res = await classifyWords(ids);
      const done = (res.results || []).map((r) => r.id);
      if (done.length) dropClassified(done);
      if (done.length < ids.length) {
        setError(`Classified ${done.length} of ${ids.length}. Try the rest again.`);
      }
    } catch (e) {
      setError(e.message || "AI classification failed.");
    } finally {
      setBusyId(null);
      setClassifyBusy(false);
    }
  };

  // ── Content editor ──────────────────────────────────────────────────────
  const startEdit = (w) => {
    setEditingId(w.id);
    setEditError(null);
    setAiBusy(null);
    setAiPreview(null);
    setDraftDef(w.definition || "");
    setDraftPlural(w.plural || "");
    setDraftExamples(
      (w.examples || []).map((e) => ({ text: e.text || "", translation: e.translation || "" }))
    );
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
    setAiPreview(null);
  };

  const updateExample = (i, field, value) =>
    setDraftExamples((prev) => prev.map((ex, idx) => (idx === i ? { ...ex, [field]: value } : ex)));
  const addExampleRow = () =>
    setDraftExamples((prev) => [...prev, { text: "", translation: "" }]);
  const removeExampleRow = (i) =>
    setDraftExamples((prev) => prev.filter((_, idx) => idx !== i));

  // Step 1: fetch a preview of everything the AI pulled (definition, verb forms,
  // plural, conjugation count) so the admin can verify before saving.
  const previewAiDefinition = async (wordId) => {
    setAiBusy("definition");
    setEditError(null);
    try {
      const res = await fetchDefinitionFromAI(wordId, { preview: true, force: true });
      setAiPreview({ wordId, data: res });
      if (res?.definition) setDraftDef(res.definition);
      if (res?.forms?.plural) setDraftPlural(res.forms.plural);
    } catch (e) {
      setEditError(e.message || "Failed to fetch definition.");
    } finally {
      setAiBusy(null);
    }
  };

  // Step 2: apply the AI result — saves the definition, verb forms,
  // conjugations and plural (mirrors the topic word fetch).
  const applyAiDefinition = async (wordId) => {
    setAiBusy("apply");
    setEditError(null);
    try {
      const res = await fetchDefinitionFromAI(wordId, { preview: false, force: true });
      const word = res?.word || {};
      const senses = word.senses || [];
      const def = senses.find((s) => s.definition)?.definition || "";
      const examples = senses.flatMap((s) => s.examples || []);
      const plural = (word.plurals && word.plurals[0]?.form) || "";
      patchItem(wordId, { definition: def, examples, plural });
      setDraftDef(def);
      setDraftPlural(plural);
      setAiPreview(null);
    } catch (e) {
      setEditError(e.message || "Failed to save AI data.");
    } finally {
      setAiBusy(null);
    }
  };

  const aiFillExamples = async (wordId) => {
    setAiBusy("examples");
    setEditError(null);
    try {
      const res = await fetchExamplesFromAI(wordId, { count: 3, preview: true });
      setDraftExamples(
        (res.examples || []).map((e) => ({ text: e.text || "", translation: e.translation || "" }))
      );
    } catch (e) {
      setEditError(e.message || "Failed to fetch examples.");
    } finally {
      setAiBusy(null);
    }
  };

  const saveEdit = async (wordId) => {
    setSaveBusy(true);
    setEditError(null);
    try {
      const cleanExamples = draftExamples
        .map((e) => ({ text: (e.text || "").trim(), translation: (e.translation || "").trim() }))
        .filter((e) => e.text);
      const word = await saveWordContent(wordId, {
        definition: draftDef.trim(),
        plural: draftPlural.trim(),
        examples: cleanExamples,
      });
      const senses = word.senses || [];
      const def = senses.find((s) => s.definition)?.definition || "";
      const examples = senses.flatMap((s) => s.examples || []);
      const plural = (word.plurals && word.plurals[0]?.form) || "";
      patchItem(wordId, { definition: def, examples, plural });
      setEditingId(null);
    } catch (e) {
      setEditError(e.message || "Failed to save.");
    } finally {
      setSaveBusy(false);
    }
  };

  const emptyMsg = {
    pending: "No words awaiting review. 🎉",
    approved: "No approved words yet.",
    rejected: "No rejected words.",
    other: "No unclassified words. 🎉",
  }[tab];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] p-4"
      onClick={onClose}
    >
      <div
        className="surface-glass dark-scope rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-ink-800">Review New Words</h2>
            <p className="text-xs text-ink-400">
              Verify the definition &amp; examples, then approve to publish.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 pt-4 shrink-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                tab === t.id
                  ? "bg-ink-500 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 scroll-elegant">
          {loading && <p className="text-sm text-ink-400">Loading…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && words.length === 0 && (
            <p className="text-sm text-ink-400">{emptyMsg}</p>
          )}

          {/* "Other" batch toolbar — classify many at once with AI */}
          {tab === "other" && !loading && words.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <p className="text-xs text-ink-400 flex-1 min-w-[8rem]">
                Set each word's type manually, or let AI classify. Writes to shared public data.
              </p>
              <button
                onClick={() =>
                  setSelected((prev) =>
                    prev.size === words.length ? new Set() : new Set(words.map((w) => w.id))
                  )
                }
                className="px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-xs"
              >
                {selected.size === words.length ? "Clear" : "Select all"}
              </button>
              <button
                disabled={selected.size === 0 || classifyBusy}
                onClick={() => aiClassify([...selected])}
                className="px-2.5 py-1 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-50"
              >
                {classifyBusy ? "Classifying…" : `AI classify selected (${selected.size})`}
              </button>
            </div>
          )}

          <ul className="list-none p-0 space-y-2">
            {words.map((w) => {
              const editing = editingId === w.id;
              return (
                <li
                  key={w.id}
                  className="surface-glass px-4 py-3 rounded-xl"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {tab === "other" && (
                          <input
                            type="checkbox"
                            checked={selected.has(w.id)}
                            onChange={() => toggleSelect(w.id)}
                            aria-label="Select for classification"
                            className="w-4 h-4 accent-violet-600"
                          />
                        )}
                        <span className="text-lg font-medium text-ink-800">{w.text}</span>
                        {w.transliteration && (
                          <span className="text-ink-400 text-sm">({w.transliteration})</span>
                        )}
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {POS_LABEL[w.pos] || w.pos || "—"}
                        </span>
                        {w.definition_requested && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"
                            title="A learner asked for a definition — the AI look-up wasn't available for them"
                          >
                            Definition requested
                          </span>
                        )}
                      </div>

                      {!editing && (
                        <>
                          {w.definition ? (
                            <p className="text-sm text-ink-500 mt-1">{w.definition}</p>
                          ) : (
                            <p className="text-sm text-ink-300 italic mt-1">No definition yet</p>
                          )}
                          {w.examples && w.examples.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {w.examples.map((ex) => (
                                <li
                                  key={ex.id || ex.text}
                                  className="surface-glass rounded-lg px-2.5 py-1.5"
                                >
                                  <p className="text-sm text-ink-700" dir="rtl">{ex.text}</p>
                                  {ex.translation && (
                                    <p className="text-xs text-ink-400 mt-0.5">{ex.translation}</p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          <button
                            onClick={() => startEdit(w)}
                            className="mt-2 px-2.5 py-1 rounded-lg border border-ink-200 text-ink-600 hover:bg-ink-50 text-xs font-medium"
                          >
                            Edit definition &amp; examples
                          </button>
                          <p className="text-xs text-ink-400 mt-2">
                            Added by {w.created_by || "unknown"}
                            {w.added_by_count > 1 && ` · in ${w.added_by_count} banks`}
                          </p>
                        </>
                      )}

                      {/* ── Editor ── */}
                      {editing && (
                        <div className="mt-3 space-y-3">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs font-semibold text-ink-600">Definition</label>
                              <button
                                disabled={!!aiBusy || saveBusy}
                                onClick={() => previewAiDefinition(w.id)}
                                className="px-2 py-0.5 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 text-xs font-medium disabled:opacity-50"
                              >
                                {aiBusy === "definition" ? "Fetching…" : "Fetch from AI"}
                              </button>
                            </div>
                            <input
                              type="text"
                              value={draftDef}
                              onChange={(e) => setDraftDef(e.target.value)}
                              placeholder="English meaning"
                              className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800"
                            />
                          </div>

                          {/* AI preview — verify all pulled data before saving */}
                          {aiPreview && aiPreview.wordId === w.id && (
                            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 space-y-1.5">
                              <p className="text-xs font-semibold text-violet-700">
                                AI result — review, then apply
                              </p>
                              {aiPreview.data.redirected_to_root && (
                                <p className="text-[11px] text-violet-600">
                                  This is a form of{" "}
                                  <span className="font-arabic" dir="rtl">{aiPreview.data.root_word_text}</span>
                                  {" "}— applying will fill the root verb (with the whole paradigm).
                                </p>
                              )}
                              <div className="text-xs text-ink-600 space-y-0.5">
                                {aiPreview.data.matched_word && (
                                  <p>
                                    <span className="text-ink-400">Matched: </span>
                                    <span dir="rtl">{aiPreview.data.matched_word}</span>
                                  </p>
                                )}
                                <p>
                                  <span className="text-ink-400">Type: </span>
                                  {aiPreview.data.part_of_speech || "—"}
                                  {aiPreview.data.word_type ? ` (${aiPreview.data.word_type})` : ""}
                                </p>
                                <p>
                                  <span className="text-ink-400">Definition: </span>
                                  {aiPreview.data.definition || <em className="text-ink-300">none</em>}
                                </p>
                                {aiPreview.data.forms && Object.keys(aiPreview.data.forms).length > 0 ? (
                                  <div>
                                    <span className="text-ink-400">Forms:</span>
                                    <ul className="ml-3 mt-0.5 space-y-0.5">
                                      {Object.entries(aiPreview.data.forms).map(([k, v]) => (
                                        <li key={k}>
                                          <span className="text-ink-400">{FORM_LABEL[k] || k}: </span>
                                          <span dir="rtl">{v}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : (
                                  <p className="text-ink-400">No verb forms / plural returned.</p>
                                )}
                                {aiPreview.data.conjugations_count > 0 && (
                                  <p className="text-ink-400">
                                    + {aiPreview.data.conjugations_count} conjugations
                                  </p>
                                )}
                                {aiPreview.data.particles && aiPreview.data.particles.length > 0 && (
                                  <div>
                                    <span className="text-ink-400">Used with:</span>
                                    <ul className="ml-3 mt-0.5 space-y-0.5">
                                      {aiPreview.data.particles.map((p, i) => (
                                        <li key={i}>
                                          <span className="font-arabic" dir="rtl">{p.particle || p.harf}</span>
                                          {p.meaning && <span className="text-ink-400"> — {p.meaning}</span>}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2 pt-1">
                                <button
                                  disabled={!!aiBusy || saveBusy}
                                  onClick={() => applyAiDefinition(w.id)}
                                  className="px-2.5 py-1 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-50"
                                >
                                  {aiBusy === "apply" ? "Saving…" : "Apply (definition, forms, plural)"}
                                </button>
                                <button
                                  disabled={!!aiBusy || saveBusy}
                                  onClick={() => setAiPreview(null)}
                                  className="px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-xs disabled:opacity-50"
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          )}

                          {w.pos === "noun" && (
                            <div>
                              <label className="text-xs font-semibold text-ink-600 block mb-1">
                                Plural form
                              </label>
                              <input
                                type="text"
                                value={draftPlural}
                                dir="rtl"
                                onChange={(e) => setDraftPlural(e.target.value)}
                                placeholder="الجمع (اختياري)"
                                className="w-full px-2.5 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800"
                              />
                            </div>
                          )}

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-xs font-semibold text-ink-600">
                                Example sentences
                              </label>
                              <button
                                disabled={!!aiBusy || saveBusy}
                                onClick={() => aiFillExamples(w.id)}
                                className="px-2 py-0.5 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 text-xs font-medium disabled:opacity-50"
                              >
                                {aiBusy === "examples" ? "Fetching…" : "Suggest 3 with AI"}
                              </button>
                            </div>
                            <div className="space-y-2">
                              {draftExamples.map((ex, i) => (
                                <div
                                  key={i}
                                  className="surface-glass rounded-lg p-2 space-y-1.5"
                                >
                                  <div className="flex items-start gap-2">
                                    <input
                                      type="text"
                                      value={ex.text}
                                      dir="rtl"
                                      onChange={(e) => updateExample(i, "text", e.target.value)}
                                      placeholder="الجملة بالعربية"
                                      className="flex-1 px-2 py-1.5 rounded border border-parchment-300 bg-white text-sm text-ink-800"
                                    />
                                    <button
                                      onClick={() => removeExampleRow(i)}
                                      className="p-1.5 rounded text-ink-300 hover:text-red-600 hover:bg-red-50 shrink-0"
                                      aria-label="Remove example"
                                      title="Remove"
                                    >
                                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={ex.translation}
                                    onChange={(e) => updateExample(i, "translation", e.target.value)}
                                    placeholder="English translation"
                                    className="w-full px-2 py-1.5 rounded border border-parchment-300 bg-white text-sm text-ink-600"
                                  />
                                </div>
                              ))}
                              <button
                                onClick={addExampleRow}
                                className="px-2.5 py-1 rounded-lg border border-ink-200 text-ink-600 hover:bg-ink-50 text-xs font-medium"
                              >
                                + Add example
                              </button>
                            </div>
                          </div>

                          {editError && <p className="text-xs text-red-600">{editError}</p>}

                          <div className="flex gap-2">
                            <button
                              disabled={saveBusy || !!aiBusy}
                              onClick={() => saveEdit(w.id)}
                              className="px-3 py-1.5 rounded-lg bg-ink-500 hover:bg-ink-600 text-white text-sm font-semibold disabled:opacity-50"
                            >
                              {saveBusy ? "Saving…" : "Save"}
                            </button>
                            <button
                              disabled={saveBusy}
                              onClick={cancelEdit}
                              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-sm disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </div>
                          <p className="text-[11px] text-ink-400">
                            Saved definition &amp; examples are public to all users.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Moderation actions */}
                    <div className="flex flex-col gap-2 shrink-0">
                      {tab === "other" ? (
                        <>
                          <div className="flex gap-1">
                            {POS_CHOICES.map((p) => (
                              <button
                                key={p.value}
                                disabled={busyId === w.id || classifyBusy}
                                onClick={() => setPosManual(w, p.value)}
                                title={`Set as ${p.label}`}
                                className="px-2 py-1 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-xs disabled:opacity-50"
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                          <button
                            disabled={busyId === w.id || classifyBusy}
                            onClick={() => aiClassify([w.id])}
                            className="px-3 py-1.5 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 text-xs font-medium disabled:opacity-50"
                          >
                            {busyId === w.id ? "Classifying…" : "AI classify"}
                          </button>
                        </>
                      ) : tab === "pending" ? (
                        <>
                          <button
                            disabled={busyId === w.id}
                            onClick={() => handleModerate(w, "approve")}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            disabled={busyId === w.id}
                            onClick={() => handleModerate(w, "reject")}
                            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-sm disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      ) : tab === "approved" ? (
                        <button
                          disabled={busyId === w.id}
                          onClick={() => handleModerate(w, "reject")}
                          className="px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm disabled:opacity-50"
                          title="Remove from public — keeps it in users' banks"
                        >
                          Unpublish
                        </button>
                      ) : confirmDeleteId === w.id ? (
                        <>
                          <button
                            disabled={busyId === w.id}
                            onClick={() => handleDelete(w)}
                            className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            Confirm delete
                          </button>
                          <button
                            disabled={busyId === w.id}
                            onClick={() => setConfirmDeleteId(null)}
                            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 text-sm disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            disabled={busyId === w.id}
                            onClick={() => handleModerate(w, "approve")}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            Re-approve
                          </button>
                          <button
                            disabled={busyId === w.id}
                            onClick={() => setConfirmDeleteId(w.id)}
                            className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
